// Time-of-click heatmap card. 6 hour-blocks x 7 days; cell intensity
// scaled 0-10 then run through helpers.heatColor.
//
// Calculations: reads CLICK_TIME_HEATMAP (precomputed in data.js).
// Mode toggle switches between clicks and reports.
//
// Postgres mapping: VIEW SELECT EXTRACT(HOUR FROM sent_at) DIV 4 AS
// hour_block, EXTRACT(DOW FROM sent_at) AS dow, COUNT(*) FROM
// simulations_sent GROUP BY 1, 2. Refresh nightly.
// Integration hook: covered by useSimsAnalyticsQuery.
import React, { useState } from 'react';
import { Box, Typography, Button, Menu, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MainCard from '../../../../components/cards/MainCard';
import { motion, AnimatePresence } from 'motion/react';
import { PERIOD_DATA } from '../../data';

const SLOTS = [
    { label: 'Early', sub: 'pre-9am' },
    { label: '9am'  }, { label: '10am' }, { label: '11am' },
    { label: '12pm' }, { label: '1pm'  }, { label: '2pm'  },
    { label: '3pm'  }, { label: '4pm'  }, { label: '5pm'  },
    { label: '6pm'  }, { label: '7pm'  }, { label: '8pm'  },
    { label: 'Night', sub: 'post-8pm' },
];

const METRIC_OPTIONS = ['Click rate', 'Report rate'];

const container = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };
const cell = { hidden: { opacity: 0, scale: 0.85 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.2, ease: 'easeOut' } } };

// Active [startMs, endMs] window: preset = window back from now; custom range
// (falsy period + explicit dates) uses those dates. Falls back to preset/90d when
// no custom dates are supplied (e.g. the summary page), so behaviour is unchanged there.
function periodWindow(period, startDate, endDate) {
    const days = { Today: 1, '7d': 7, '30d': 30, '90d': 90, '6m': 182, '12m': 365 }[period];
    const now = Date.now();
    if (days != null) return [now - days * 86400000, now];
    const s = startDate ? Date.parse(startDate + 'T00:00:00Z') : (now - 90 * 86400000);
    const e = endDate ? Date.parse(endDate + 'T23:59:59Z') : now;
    return [s, e];
}

export default function HeatmapCard({ go, period, log, heatmapAgg, strictAgg, startDate, endDate }) {
    const theme = useTheme();
    const [metric, setMetric] = useState('Click rate');
    const [anchorEl, setAnchorEl] = useState(null);

    const pd = PERIOD_DATA[period] || PERIOD_DATA['7d'];
    const scopedValues = React.useMemo(() => {
        if (Array.isArray(heatmapAgg) && (heatmapAgg.length || strictAgg) && metric === 'Click rate') {
            const cells = new Array(7 * 24).fill(0);
            for (const c of heatmapAgg) { const day = (c.weekday + 6) % 7; cells[day * 24 + c.hour] = c.clicks || 0; }
            return cells;
        }
        if (!log) return null;
        const [winStart, winEnd] = periodWindow(period, startDate, endDate);
        const cells = new Array(7 * 24).fill(0);
        for (const e of log) {
            const t = Date.parse((e.lastActive || (e.dateSent + 'T12:00:00Z')));
            if (Number.isNaN(t) || t < winStart || t > winEnd) continue;
            const isClick = e.status === 'clicked' || e.status === 'clicked_2nd' || e.status === 'attachment';
            const isReport = e.status === 'reported';
            if (metric === 'Click rate' ? !isClick : !isReport) continue;
            const d = new Date(t);
            const day = (d.getUTCDay() + 6) % 7;
            const hour = d.getUTCHours();
            cells[day * 24 + hour] += 1;
        }
        return cells;
    }, [log, period, metric, heatmapAgg, strictAgg]);
    const values = scopedValues || (metric === 'Click rate' ? pd.heatClick : pd.heatReport);
    const max = Math.max(...values);
    const min = Math.min(...values);
    const primary = theme.palette.primary.main;

    const cellBg = (val) => {
        const t = (val - min) / (max - min);
        const opacity = metric === 'Report rate'
            ? 0.1 + (1 - t) * 0.82
            : 0.1 + t * 0.82;
        return primary + Math.round(opacity * 255).toString(16).padStart(2, '0');
    };

    const cellText = (val) => {
        const t = (val - min) / (max - min);
        const effectiveT = metric === 'Report rate' ? 1 - t : t;
        return effectiveT > 0.55
            ? theme.palette.primary.contrastText
            : theme.palette.primary.main;
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            style={{ height: '100%' }}
        >
            <MainCard content={false} sx={{ bgcolor: 'background.paper', height: '100%', overflow: 'hidden' }} border={false} boxShadow={true}>
                <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5, flexShrink: 0 }}>
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                                {metric === 'Report rate' ? 'When users report' : 'When users click'}
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                                {metric === 'Report rate' ? 'Darker = higher report rate' : 'Darker = higher rate'} - by time of day
                            </Typography>
                        </Box>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                            sx={{
                                borderRadius: '4px', borderColor: 'secondary.main', color: 'secondary.main',
                                fontSize: 11, py: '3px', px: '10px', textTransform: 'none', backgroundColor: 'transparent',
                                '&:hover': { backgroundColor: 'secondary.main', borderColor: 'secondary.main', color: 'common.white' },
                            }}
                        >
                            {metric}
                        </Button>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={() => setAnchorEl(null)}
                            PaperProps={{
                                sx: { border: '1px solid', borderColor: 'secondary.main', borderRadius: '4px', boxShadow: 'none', mt: 0.5 },
                            }}
                        >
                            {METRIC_OPTIONS.map((o) => (
                                <MenuItem
                                    key={o}
                                    onClick={() => { setMetric(o); setAnchorEl(null); }}
                                    sx={{
                                        fontSize: 11, borderRadius: '4px', mx: 0.5,
                                        color: o === metric ? 'common.white' : 'secondary.main',
                                        backgroundColor: o === metric ? 'secondary.main' : 'transparent',
                                        '&:hover': { backgroundColor: 'secondary.main', color: 'common.white' },
                                    }}
                                >
                                    {o}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={metric}
                            variants={container}
                            initial="hidden"
                            animate="visible"
                            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(44px, 1fr))', gap: 4, marginTop: 8 }}
                        >
                            {SLOTS.map((slot, i) => (
                                <motion.div key={slot.label} variants={cell} style={{ minWidth: 0 }}>
                                    <Box
                                        sx={{
                                            borderRadius: '5px',
                                            height: 48,
                                            position: 'relative',
                                            background: cellBg(values[i]),
                                        }}
                                    >
                                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {slot.sub ? (
                                                <Typography sx={{ fontSize: 10, fontWeight: 600, color: cellText(values[i]), textAlign: 'center', lineHeight: 1.2, px: '4px' }}>
                                                    {slot.label}
                                                </Typography>
                                            ) : (
                                                <Typography sx={{ fontSize: 12, color: cellText(values[i]), textAlign: 'center', lineHeight: 1 }}>
                                                    <Box component="span" sx={{ fontWeight: 700 }}>
                                                        {slot.label.match(/\d+/)?.[0]}
                                                    </Box>
                                                    <Box component="span" sx={{ fontWeight: 400, fontSize: 9 }}>
                                                        {slot.label.match(/[a-z]+/)?.[0]}
                                                    </Box>
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </motion.div>
                            ))}
                        </motion.div>
                    </AnimatePresence>
                </Box>
            </MainCard>
        </motion.div>
    );
}
